<template>
    <div class="stock-stepper" :class="{ 'is-infinite': isInfinite }" @focusout="onFocusOut" @keydown="onKeydown">
        <button
            type="button"
            class="stock-stepper__btn"
            tabindex="-1"
            :disabled="isDecrementDisabled"
            :aria-label="$t('stock.stepper.decrement')"
            @mousedown.prevent
            @click="step(-1)"
        >
            <Minus :size="14" />
        </button>
        <input
            ref="input"
            class="stock-stepper__input"
            type="text"
            inputmode="numeric"
            autocomplete="off"
            :value="draft"
            :style="{ width: inputWidth }"
            :aria-label="$t('stock.edit')"
            :title="$t('stock.stepper.keyboard_hint')"
            @input="onInput"
            @focus="selectAll"
        />
        <button
            type="button"
            class="stock-stepper__btn"
            tabindex="-1"
            :aria-label="$t('stock.stepper.increment')"
            @mousedown.prevent
            @click="step(1)"
        >
            <Plus :size="14" />
        </button>
        <button
            type="button"
            class="stock-stepper__btn stock-stepper__btn--inf"
            tabindex="-1"
            :class="{ 'is-active': isInfinite }"
            :title="$t('stock.infinite')"
            :aria-label="$t('stock.infinite')"
            :aria-pressed="isInfinite ? 'true' : 'false'"
            @mousedown.prevent
            @click="setInfinite"
        >
            {{ INFINITY }}
        </button>
    </div>
</template>

<script>
import { Minus, Plus } from 'lucide-vue'
import { INFINITY, formatStock } from './stockUtils'

export default {
    name: 'StockStepper',
    components: { Minus, Plus },
    props: {
        value: {
            type: Number,
            default: null, // null = ∞
        },
        min: {
            type: Number,
            default: 0,
        },
        commitOnBlur: {
            type: Boolean,
            default: true,
        },
        autofocus: {
            type: Boolean,
            default: false,
        },
        /** Mode lot : Tab / Shift+Tab sont proposés à l'hôte (`navigate`) pour sauter d'une ligne à l'autre. */
        trapTab: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            INFINITY,
            draft: formatStock(this.value),
        }
    },
    computed: {
        isInfinite() {
            return this.value === null || this.value === undefined
        },
        isDecrementDisabled() {
            return !this.isInfinite && Number(this.value) <= this.min
        },
        inputWidth() {
            const length = String(this.draft || '').length
            return length <= 2 ? '32px' : `${length + 1}ch`
        },
    },
    watch: {
        value(newValue) {
            const formatted = formatStock(newValue)
            if (formatted !== this.draft) this.draft = formatted
        },
    },
    methods: {
        selectAll(event) {
            if (event && event.target && typeof event.target.select === 'function') {
                event.target.select()
            }
        },
        onInput(event) {
            const raw = String(event.target.value || '')
            const digits = raw.replace(/\D+/g, '')
            // Garde l'état visuel cohérent (pas de lettres ni de « ∞ » mélangé à des chiffres)
            if (raw !== digits) event.target.value = digits
            this.draft = digits
            if (digits === '') return
            const parsed = Math.max(this.min, parseInt(digits, 10))
            if (parsed !== this.value) this.$emit('input', parsed)
        },
        step(direction) {
            const base = this.isInfinite ? 0 : Number(this.value)
            const next = Math.max(this.min, base + direction)
            if (next === this.value) return
            this.draft = String(next)
            this.$emit('input', next)
        },
        setInfinite() {
            if (this.isInfinite) return
            this.draft = INFINITY
            this.$emit('input', null)
        },
        currentValue() {
            if (this.draft === '') return this.value
            if (this.draft === INFINITY) return null
            const parsed = parseInt(this.draft, 10)
            return Number.isNaN(parsed) ? this.value : Math.max(this.min, parsed)
        },
        commit() {
            this.draft = formatStock(this.currentValue())
            this.$emit('commit', this.currentValue())
        },
        cancel() {
            this.$emit('cancel')
        },
        onKeydown(event) {
            if (event.key === 'Enter') {
                event.preventDefault()
                this.commit()
            } else if (event.key === 'Escape' || event.key === 'Esc') {
                event.preventDefault()
                event.stopPropagation()
                this.cancel()
            } else if (event.key === 'Tab' && this.trapTab) {
                // L'hôte appelle event.preventDefault() s'il a déplacé le focus vers une autre ligne
                this.$emit('navigate', { direction: event.shiftKey ? -1 : 1, event })
            } else if (event.key === '*' || event.key === '∞' || event.key === 'i' || event.key === 'I') {
                event.preventDefault()
                this.setInfinite()
                this.$nextTick(this.selectInput)
            } else if (event.key === 'ArrowUp') {
                event.preventDefault()
                this.step(1)
            } else if (event.key === 'ArrowDown') {
                event.preventDefault()
                this.step(-1)
            }
        },
        onFocusOut(event) {
            if (!this.commitOnBlur) return
            const next = event.relatedTarget
            if (next && this.$el.contains(next)) return
            // Laisse le navigateur finir le déplacement de focus avant de prévenir l'hôte
            this.$nextTick(() => {
                if (this.$el && this.$el.contains(document.activeElement)) return
                this.commit()
            })
        },
        selectInput() {
            const input = this.$refs.input
            if (input && document.activeElement === input) input.select()
        },
        focus() {
            const input = this.$refs.input
            if (!input) return
            input.focus()
            input.select()
        },
    },
    mounted() {
        if (this.autofocus) this.$nextTick(this.focus)
    },
}
</script>

<style scoped lang="scss">
.stock-stepper {
    display: inline-flex;
    align-items: stretch;
    height: 32px;
    border: 1.5px solid #7d2dbd;
    border-radius: 10px;
    background: #fff;
    overflow: hidden;
    box-shadow: 0 2px 10px rgba(125, 45, 189, 0.15);
}

.stock-stepper__btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    border: 0;
    background: transparent;
    color: #3f1761;
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover:not(:disabled) {
        background: #f3eafa;
    }

    &:disabled {
        color: #c4b5d4;
        cursor: not-allowed;
    }
}

.stock-stepper__btn--inf {
    font-size: 16px;
    line-height: 1;
    border-left: 1px solid #e2d0f3;

    &.is-active {
        background: #ecdcfa;
        color: #7d2dbd;
    }
}

.stock-stepper__input {
    min-width: 32px;
    border: 0;
    border-left: 1px solid #e2d0f3;
    border-right: 1px solid #e2d0f3;
    text-align: center;
    font-size: 14px;
    font-variant-numeric: tabular-nums;
    color: #021832;
    background: #fff;
    outline: none;
    padding: 0 2px;
}

.is-infinite .stock-stepper__input {
    color: #6b7280;
}

.dark .stock-stepper {
    background: #1a1f37;
}

.dark .stock-stepper__input {
    background: #1a1f37;
    color: #fff;
}
</style>
